"use client";

import * as React from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useTraceway, useTracewayAttributes } from "@tracewayapp/react";

import { useAuthenticatedUser } from "@/app/hooks/use-authenticated-user";
import {
  AUTH_CHANGED_EVENT,
  readBrowserSession,
} from "@/app/services/auth-storage";

const TRACEWAY_SESSION_STORAGE_KEY = "pteultra-traceway-session-id";
const TRACEWAY_SESSION_STARTED_AT_STORAGE_KEY = "pteultra-traceway-session-started-at";
const CLICK_THROTTLE_MS = 800;

type NavigatorWithConnection = Navigator & {
  connection?: {
    effectiveType?: string;
    saveData?: boolean;
    downlink?: number;
    rtt?: number;
  };
};

function compactStringMap(values: Record<string, string | number | boolean | null | undefined>) {
  return Object.fromEntries(
    Object.entries(values).flatMap(([key, value]) => {
      if (value == null) {
        return [];
      }

      const normalized = typeof value === "string" ? value.trim() : String(value);
      return normalized ? [[key, normalized]] : [];
    })
  ) as Record<string, string>;
}

function createId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getOrCreateSessionValue(storageKey: string, fallbackValue: string) {
  if (typeof window === "undefined") {
    return fallbackValue;
  }

  try {
    const existingValue = window.sessionStorage.getItem(storageKey)?.trim();
    if (existingValue) {
      return existingValue;
    }

    window.sessionStorage.setItem(storageKey, fallbackValue);
  } catch {
    return fallbackValue;
  }

  return fallbackValue;
}

function resolveLocaleCountryCode(locales: readonly string[], fallbackLocale: string) {
  const localeCandidates = [...locales, fallbackLocale].filter(Boolean);
  const LocaleCtor = (Intl as typeof Intl & {
    Locale?: new (tag: string) => { region?: string };
  }).Locale;

  for (const locale of localeCandidates) {
    if (!locale) {
      continue;
    }

    try {
      const region = LocaleCtor ? new LocaleCtor(locale).region : undefined;
      if (region) {
        return region.toUpperCase();
      }
    } catch {
      // Ignore invalid locale tags and try the next candidate.
    }

    const parts = locale.split("-");
    const regionCandidate = parts.at(-1)?.trim();
    if (regionCandidate && /^[A-Za-z]{2}$/.test(regionCandidate)) {
      return regionCandidate.toUpperCase();
    }
  }

  return "";
}

function getDeviceType() {
  if (typeof window === "undefined") {
    return "";
  }

  const userAgent = window.navigator.userAgent;
  const screenWidth = window.screen?.width ?? window.innerWidth;

  if (/tablet|ipad/i.test(userAgent) || (screenWidth >= 768 && screenWidth <= 1024 && "ontouchstart" in window)) {
    return "tablet";
  }

  if (/mobi|android|iphone|ipod/i.test(userAgent) || screenWidth < 768) {
    return "mobile";
  }

  return "desktop";
}

function maskEmail(email: string) {
  const normalizedEmail = email.trim();
  if (!normalizedEmail.includes("@")) {
    return "";
  }

  const [localPart, domainPart] = normalizedEmail.split("@");
  if (!localPart || !domainPart) {
    return "";
  }

  const maskedLocalPart = localPart.length <= 2
    ? `${localPart.slice(0, 1)}*`
    : `${localPart.slice(0, 2)}***`;

  return `${maskedLocalPart}@${domainPart}`;
}

function getNavigationMetrics() {
  if (typeof window === "undefined") {
    return null;
  }

  const navigationEntry = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  if (!navigationEntry) {
    return null;
  }

  return {
    navigationType: navigationEntry.type,
    redirectCount: navigationEntry.redirectCount,
    dnsLookupMs: Math.round(navigationEntry.domainLookupEnd - navigationEntry.domainLookupStart),
    tcpConnectMs: Math.round(navigationEntry.connectEnd - navigationEntry.connectStart),
    requestMs: Math.round(navigationEntry.responseStart - navigationEntry.requestStart),
    responseMs: Math.round(navigationEntry.responseEnd - navigationEntry.responseStart),
    domInteractiveMs: Math.round(navigationEntry.domInteractive),
    domContentLoadedMs: Math.round(
      navigationEntry.domContentLoadedEventEnd - navigationEntry.startTime
    ),
    pageLoadMs: Math.round(navigationEntry.loadEventEnd - navigationEntry.startTime),
  };
}

function getPaintMetric(entryName: string) {
  if (typeof window === "undefined") {
    return undefined;
  }

  const paintEntry = performance.getEntriesByName(entryName)[0] as PerformanceEntry | undefined;
  if (!paintEntry) {
    return undefined;
  }

  return Math.round(paintEntry.startTime);
}

function describeElement(target: EventTarget | null) {
  if (!(target instanceof Element)) {
    return {};
  }

  const closestInteractive = target.closest("button, a, input, select, textarea, label, [role], [data-testid]");
  const element = closestInteractive ?? target;

  return compactStringMap({
    tagName: element.tagName.toLowerCase(),
    id: element.id || undefined,
    role: element.getAttribute("role") || undefined,
    name: element.getAttribute("name") || undefined,
    type: element.getAttribute("type") || undefined,
    href: element instanceof HTMLAnchorElement ? element.href : undefined,
    dataTestId: element.getAttribute("data-testid") || undefined,
  });
}

export default function TracewayActivityTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isAuthenticated = useAuthenticatedUser();
  const { recordAction } = useTraceway();

  const [clientSessionId, setClientSessionId] = React.useState("");
  const [sessionStartedAt, setSessionStartedAt] = React.useState("");
  const [userEmail, setUserEmail] = React.useState("");
  const hasRecordedPageLoadRef = React.useRef(false);
  const routeRef = React.useRef("");
  const lastClickAtRef = React.useRef(0);
  const previousAuthStateRef = React.useRef<boolean | null>(null);

  const queryString = searchParams?.toString() ?? "";
  const route = queryString ? `${pathname ?? "/"}?${queryString}` : pathname ?? "/";

  React.useEffect(() => {
    const nextSessionId = getOrCreateSessionValue(TRACEWAY_SESSION_STORAGE_KEY, createId());
    const nextSessionStartedAt = getOrCreateSessionValue(
      TRACEWAY_SESSION_STARTED_AT_STORAGE_KEY,
      new Date().toISOString()
    );

    setClientSessionId(nextSessionId);
    setSessionStartedAt(nextSessionStartedAt);
  }, []);

  React.useEffect(() => {
    const syncUserEmail = () => {
      setUserEmail(readBrowserSession()?.email ?? '');
    };

    syncUserEmail();
    window.addEventListener(AUTH_CHANGED_EVENT, syncUserEmail);
    window.addEventListener("focus", syncUserEmail);

    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, syncUserEmail);
      window.removeEventListener("focus", syncUserEmail);
    };
  }, []);

  const tracewayAttributes = React.useMemo(() => {
    if (typeof window === "undefined") {
      return compactStringMap({
        authenticated: isAuthenticated,
        clientSessionId,
        sessionStartedAt,
      });
    }

    const navigatorWithConnection = window.navigator as NavigatorWithConnection;
    const localeCountryCode = resolveLocaleCountryCode(
      navigatorWithConnection.languages ?? [],
      navigatorWithConnection.language ?? ""
    );

    return compactStringMap({
      authenticated: isAuthenticated,
      clientSessionId,
      sessionStartedAt,
      host: window.location.host,
      origin: window.location.origin,
      deviceType: getDeviceType(),
      touchPoints: navigatorWithConnection.maxTouchPoints,
      cookiesEnabled: navigatorWithConnection.cookieEnabled,
      online: navigatorWithConnection.onLine,
      localeCountryCode,
      countrySource: localeCountryCode ? "locale" : "unavailable",
      effectiveConnectionType: navigatorWithConnection.connection?.effectiveType,
      saveData: navigatorWithConnection.connection?.saveData,
      networkDownlinkMbps: navigatorWithConnection.connection?.downlink,
      networkRttMs: navigatorWithConnection.connection?.rtt,
      screenOrientation: window.screen?.orientation?.type,
      pagePath: pathname ?? "/",
      pageQuery: queryString,
      userEmailMasked: maskEmail(userEmail),
      userEmailDomain: userEmail.includes("@") ? userEmail.split("@")[1]?.trim() : undefined,
    });
  }, [clientSessionId, isAuthenticated, pathname, queryString, sessionStartedAt, userEmail]);

  useTracewayAttributes(tracewayAttributes);

  React.useEffect(() => {
    if (!clientSessionId || typeof window === "undefined") {
      return;
    }

    if (hasRecordedPageLoadRef.current) {
      return;
    }

    hasRecordedPageLoadRef.current = true;

    const navigationMetrics = getNavigationMetrics();
    recordAction("performance", "page_loaded", {
      route,
      href: window.location.href,
      referrer: document.referrer || undefined,
      sessionId: clientSessionId,
      ...navigationMetrics,
      firstPaintMs: getPaintMetric("first-paint"),
      firstContentfulPaintMs: getPaintMetric("first-contentful-paint"),
    });
  }, [clientSessionId, recordAction, route]);

  React.useEffect(() => {
    if (!clientSessionId || typeof window === "undefined") {
      return;
    }

    if (routeRef.current === route) {
      return;
    }

    const previousRoute = routeRef.current;
    routeRef.current = route;

    recordAction("navigation", previousRoute ? "route_changed" : "page_view", {
      route,
      previousRoute: previousRoute || undefined,
      pathname: pathname ?? "/",
      queryString: queryString || undefined,
      href: window.location.href,
      sessionId: clientSessionId,
    });
  }, [clientSessionId, pathname, queryString, recordAction, route]);

  React.useEffect(() => {
    if (!clientSessionId || typeof document === "undefined") {
      return;
    }

    const handleVisibilityChange = () => {
      recordAction("lifecycle", "visibility_changed", {
        route,
        sessionId: clientSessionId,
        visibilityState: document.visibilityState,
      });
    };

    const handleOnline = () => {
      recordAction("lifecycle", "connectivity_changed", {
        route,
        sessionId: clientSessionId,
        online: navigator.onLine,
      });
    };

    const handlePageHide = () => {
      recordAction("lifecycle", "page_hidden", {
        route,
        sessionId: clientSessionId,
      });
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOnline);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOnline);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [clientSessionId, recordAction, route]);

  React.useEffect(() => {
    if (!clientSessionId || typeof document === "undefined") {
      return;
    }

    const handleClick = (event: MouseEvent) => {
      const now = Date.now();
      if (now - lastClickAtRef.current < CLICK_THROTTLE_MS) {
        return;
      }

      lastClickAtRef.current = now;
      recordAction("user", "click", {
        route,
        sessionId: clientSessionId,
        ...describeElement(event.target),
      });
    };

    const handleSubmit = (event: SubmitEvent) => {
      recordAction("user", "form_submit", {
        route,
        sessionId: clientSessionId,
        ...describeElement(event.target),
      });
    };

    document.addEventListener("click", handleClick, { passive: true, capture: true });
    document.addEventListener("submit", handleSubmit, { capture: true });

    return () => {
      document.removeEventListener("click", handleClick, { capture: true });
      document.removeEventListener("submit", handleSubmit, { capture: true });
    };
  }, [clientSessionId, recordAction, route]);

  React.useEffect(() => {
    if (!clientSessionId) {
      return;
    }

    if (previousAuthStateRef.current == null) {
      previousAuthStateRef.current = isAuthenticated;
      recordAction("auth", "auth_state_detected", {
        route,
        sessionId: clientSessionId,
        authenticated: isAuthenticated,
      });
      return;
    }

    if (previousAuthStateRef.current === isAuthenticated) {
      return;
    }

    previousAuthStateRef.current = isAuthenticated;
    recordAction("auth", "auth_state_changed", {
      route,
      sessionId: clientSessionId,
      authenticated: isAuthenticated,
    });
  }, [clientSessionId, isAuthenticated, recordAction, route]);

  return null;
}
